import React, { useState, useEffect, useRef } from 'react';
import { qaService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to convert newlines to HTML br tags
const formatTextWithLineBreaks = (text) => {
  if (!text) return '';
  return text.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < text.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));
};

// AutoComplete Multi-Select Component
const AutoCompleteMultiSelect = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option =>
    !selectedValues.includes(option.id) &&
    option.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
    setFocusedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputBlur = () => {
    // Delay closing to allow for option clicks
    setTimeout(() => setIsOpen(false), 200);
  };

  const handleOptionClick = (option) => {
    const newSelectedValues = [...selectedValues, option.id];
    onChange(newSelectedValues);
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleRemoveTag = (tagId) => {
    const newSelectedValues = selectedValues.filter(id => id !== tagId);
    onChange(newSelectedValues);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleOptionClick(filteredOptions[focusedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const selectedTags = options.filter(option => selectedValues.includes(option.id));

  return (
    <div className="relative">
      <div className="min-h-[40px] p-2 border rounded bg-white flex flex-wrap gap-1">
        {/* Selected tags */}
        {selectedTags.map(tag => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2 py-1 text-xs rounded-full"
            style={{ backgroundColor: tag.color + '20', color: tag.color }}
          >
            {tag.name}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag.id)}
              className="ml-1 hover:bg-black hover:bg-opacity-10 rounded-full w-4 h-4 flex items-center justify-center"
            >
              ×
            </button>
          </span>
        ))}

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] outline-none text-sm"
          disabled={disabled}
        />
      </div>

      {/* Dropdown */}
      {isOpen && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option.id}
              className={`px-3 py-2 cursor-pointer text-sm hover:bg-gray-100 ${
                index === focusedIndex ? 'bg-gray-100' : ''
              }`}
              onClick={() => handleOptionClick(option)}
            >
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: option.color }}
              ></span>
              {option.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QAKnowledgeBaseSection = () => {
  const { t } = useLanguage();
  const [qaItems, setQAItems] = useState([]);
  const [qaTags, setQATags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentQAItem, setCurrentQAItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingHTML, setIsExportingHTML] = useState(false);
  const fileInputRef = useRef(null);
  const [languageFilter, setLanguageFilter] = useState('all');
  const [filteredQAItems, setFilteredQAItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [tagFilter, setTagFilter] = useState('all');
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ question: '', answer: '', language: '' });
  // Tag management state
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);
  const [tagFormData, setTagFormData] = useState({ name: '', color: '#3B82F6' });

  // Language codes supported by the system
  const languageCodes = [
    { value: 'zh_HK', label: t('qa.chineseTraditional') },
    { value: 'zh_CN', label: t('qa.chineseSimplified') },
    { value: 'en', label: 'English' },
    { value: 'en_US', label: 'English (US)' },
  ];

  // Attach rule options
  const attachRules = [
    { value: 'always', label: t('qa.attachRuleAlwaysLabel') },
    { value: 'if_language_match', label: t('qa.attachRuleIfMatchLabel') },
    { value: 'in_review', label: t('qa.attachRuleOnHoldLabel') }
  ];

  // Initial form state for a new QA item
  const initialFormState = {
    language_code: 'zh_HK',
    question: '',
    answer: '',
    attach_rule: 'always',
    tag_ids: []
  };

  const [formData, setFormData] = useState(initialFormState);

  // Handler for opening dialog with full content
  const handleOpenDialog = (qaItem) => {
    const language = languageCodes.find(l => l.value === qaItem.language_code)?.label || qaItem.language_code;
    setDialogContent({
      question: qaItem.question,
      answer: qaItem.answer,
      language: language
    });
    setShowDialog(true);
  };

  // Handler for closing the dialog
  const handleCloseDialog = () => {
    setShowDialog(false);
  };



  // Handle ESC key press to close dialog
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && (showDialog || showTagDialog)) {
        if (showDialog) handleCloseDialog();
        if (showTagDialog) handleCloseTagDialog();
      }
    };

    // Add event listener when dialog is shown
    if (showDialog || showTagDialog) {
      document.addEventListener('keydown', handleEscKey);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDialog, showTagDialog]);

  useEffect(() => {
    loadQAItems();
    loadQATags();
  }, []);

  // Filter QA items when language filter, search query, or QA items change
  useEffect(() => {
    let items = [...qaItems];

    // Apply language filter
    if (languageFilter !== 'all') {
      items = items.filter(item => item.language_code === languageFilter);
    }

    // Apply tag filter
    if (tagFilter !== 'all') {
      items = items.filter(item =>
        item.tags && item.tags.some(tag => tag.id === tagFilter)
      );
    }

    // Apply search filter if there's a search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query)
      );
    }

    // Sort items with zh-HK first, then by other language codes
    items.sort((a, b) => {
      // Put zh-HK first
      if (a.language_code === 'zh_HK' && b.language_code !== 'zh_HK') return -1;
      if (a.language_code !== 'zh_HK' && b.language_code === 'zh_HK') return 1;

      // For items with the same language, sort by creation date (newest first)
      return 0;
    });

    setFilteredQAItems(items);
  }, [languageFilter, qaItems, searchQuery, tagFilter]);

  const loadQAItems = async (search = '') => {
    try {
      setLoading(true);
      let response;

      if (search.trim()) {
        setIsSearching(true);
        response = await qaService.searchQAItems(search);
      } else {
        response = await qaService.getQAItems();
      }

      // Handle new API response structure
      if (response.success && response.data) {
        // Set zh-HK as default language filter if there are zh-HK items
        const zhHkItems = response.data.qa_items.filter(item => item.language_code === 'zh_HK');
        if (zhHkItems.length > 0 && languageFilter === 'all') {
          setLanguageFilter('zh_HK');
        }

        setQAItems(response.data.qa_items);
      } else {
        console.error('API response error:', response.message);
        setError(response.message || t('qa.loadingItemsFailed'));
      }
      setIsSearching(false);
    } catch (err) {
      console.error('Error loading QA knowledge base items:', err);
      setError(t('qa.loadingItemsFailed'));
      setIsSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const loadQATags = async () => {
    try {
      const response = await qaService.getQATags();
      if (response.success && response.data) {
        setQATags(response.data.tags || []);
      }
    } catch (err) {
      console.error('Error loading QA tags:', err);
      // Don't show error for tags, just log it
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadQAItems(searchQuery);
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (!e.target.value.trim()) {
      // If search query is cleared, load all items
      loadQAItems();
    }
  };

  const handleLanguageFilterChange = (e) => {
    setLanguageFilter(e.target.value);
  };

  const handleTagFilterChange = (e) => {
    setTagFilter(e.target.value);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const params = {};
      if (tagFilter && tagFilter !== 'all') {
        params.tagId = tagFilter;
      }
      const data = await qaService.exportQAItems(params);
      const content = JSON.stringify(data.items || [], null, 2);
      const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `qa-for-pricing-${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccessMessage(t('qa.exportSuccess') || 'Exported QA items successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error exporting QA items:', err);
      setError(t('qa.exportFailed') || 'Failed to export QA items');
      setTimeout(() => setError(null), 4000);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportHTML = async () => {
    try {
      setIsExportingHTML(true);
      const params = {};
      if (tagFilter && tagFilter !== 'all') {
        params.tagId = tagFilter;
      }
      const blob = await qaService.exportQAItemsToHTML(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `qa-export-${timestamp}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccessMessage(t('qa.exportHTMLSuccess') || 'Exported QA items as HTML successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error exporting QA items to HTML:', err);
      setError(t('qa.exportHTMLFailed') || 'Failed to export QA items as HTML');
      setTimeout(() => setError(null), 4000);
    } finally {
      setIsExportingHTML(false);
    }
  };

  const handleAddNew = () => {
    setFormData(initialFormState);
    setCurrentQAItem(null);
    setIsEditing(true);
  };

  const handleEdit = (qaItem) => {
    setFormData({
      language_code: qaItem.language_code,
      question: qaItem.question,
      answer: qaItem.answer,
      attach_rule: qaItem.attach_rule,
      tag_ids: qaItem.tags ? qaItem.tags.map(tag => tag.id) : []
    });
    setCurrentQAItem(qaItem);
    setIsEditing(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagSelectionChange = (newSelectedValues) => {
    setFormData(prev => ({
      ...prev,
      tag_ids: newSelectedValues
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.question.trim() || !formData.answer.trim()) {
      setError(t('qa.questionAnswerRequired'));
      return;
    }

    if (formData.answer.length > 1000) {
      setError(t('qa.answerMaxLengthExceeded') || 'Answer must be 1000 characters or less');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      let response;
      if (currentQAItem) {
        // Update existing QA item
        response = await qaService.updateQAItem(currentQAItem.id, formData);
      } else {
        // Create new QA item
        response = await qaService.createQAItem(formData);
      }

      // Handle API response structure
      if (response.success) {
        setSuccessMessage(response.message || (currentQAItem ? t('qa.itemUpdatedSuccess') : t('qa.itemAddedSuccess')));

        // Reset and refresh
        setIsEditing(false);
        setCurrentQAItem(null);
        setFormData(initialFormState);
        loadQAItems();

        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(response.message || t('qa.itemSaveFailed'));
      }
    } catch (err) {
      console.error('Error saving QA knowledge base item:', err);
      setError(t('qa.itemSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentQAItem(null);
    setFormData(initialFormState);
    setError(null);
  };

  const handleDelete = async (qaId) => {
    if (!window.confirm(t('qa.deleteItemConfirm'))) {
      return;
    }

    try {
      setIsSaving(true);
      const response = await qaService.deleteQAItem(qaId);

      // Handle API response structure
      if (response.success) {
        setSuccessMessage(response.message || t('qa.itemDeletedSuccess'));
        loadQAItems();

        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(response.message || t('qa.itemDeleteFailed'));
      }
    } catch (err) {
      console.error('Error deleting QA knowledge base item:', err);
      setError(t('qa.itemDeleteFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (qaItem) => {
    try {
      setIsSaving(true);
      const newStatus = qaItem.is_active ? 0 : 1;
      const response = await qaService.updateQAItem(qaItem.id, { is_active: newStatus });

      // Handle API response structure
      if (response.success) {
        setSuccessMessage(response.message || (newStatus ? t('qa.itemActivatedSuccess') : t('qa.itemDeactivatedSuccess')));
        loadQAItems();

        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(response.message || t('qa.itemStatusUpdateFailed'));
      }
    } catch (err) {
      console.error('Error updating QA knowledge base item status:', err);
      setError(t('qa.itemStatusUpdateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (must be JSON)
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError(t('qa.validJsonRequired'));
      return;
    }

    try {
      setIsImporting(true);
      setError(null);

      // Read the file content
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);

          // Check if data is an array
          if (!Array.isArray(jsonData)) {
            setError(t('qa.jsonArrayRequired'));
            setIsImporting(false);
            return;
          }

          // Validate each item in the array
          const validItems = jsonData.filter(item =>
            item.language && item.question && item.answer
          );

          if (validItems.length === 0) {
            setError(t('qa.noValidItems'));
            setIsImporting(false);
            return;
          }

          // Import items
          const result = await qaService.importQAItems(validItems);

          // Handle API response structure
          if (result.success) {
            setSuccessMessage(result.message);
            loadQAItems();
          } else {
            setError(result.message || t('qa.importFailed'));
          }

        } catch (err) {
          console.error('Error parsing JSON file:', err);
          setError(t('qa.jsonParseError'));
        } finally {
          setIsImporting(false);
        }
      };

      reader.onerror = () => {
        setError(t('qa.fileReadError'));
        setIsImporting(false);
      };

      reader.readAsText(file);
    } catch (err) {
      console.error('Error importing QA items:', err);
      setError(t('qa.importFailed'));
      setIsImporting(false);
    }

    // Reset the file input
    e.target.value = null;
  };

  // Tag management functions
  const handleAddTag = () => {
    setTagFormData({ name: '', color: '#3B82F6' });
    setCurrentTag(null);
    setIsEditingTag(true);
    setShowTagDialog(true);
  };

  const handleShowTagDialog = () => {
    setIsEditingTag(false);
    setCurrentTag(null);
    setTagFormData({ name: '', color: '#3B82F6' });
    setShowTagDialog(true);
  };

  const handleEditTag = (tag) => {
    setTagFormData({ name: tag.name, color: tag.color });
    setCurrentTag(tag);
    setIsEditingTag(true);
    setShowTagDialog(true);
  };

  const handleTagFormChange = (e) => {
    const { name, value } = e.target;
    setTagFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagSubmit = async (e) => {
    e.preventDefault();

    if (!tagFormData.name.trim()) {
      setError(t('qa.tagNameRequired'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      if (currentTag) {
        // Update existing tag
        await qaService.updateQATag(currentTag.id, tagFormData);
        setSuccessMessage(t('qa.tagUpdatedSuccess'));
      } else {
        // Create new tag
        await qaService.createQATag(tagFormData);
        setSuccessMessage(t('qa.tagCreatedSuccess'));
      }

      // Reset and refresh
      setIsEditingTag(false);
      setCurrentTag(null);
      setTagFormData({ name: '', color: '#3B82F6' });
      setShowTagDialog(false);
      loadQATags();

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error saving tag:', err);
      setError(t('qa.tagSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTagCancel = () => {
    setIsEditingTag(false);
    setCurrentTag(null);
    setTagFormData({ name: '', color: '#3B82F6' });
    setShowTagDialog(false);
    setError(null);
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm(t('qa.deleteTagConfirm'))) {
      return;
    }

    try {
      setIsSaving(true);
      await qaService.deleteQATag(tagId);
      setSuccessMessage(t('qa.tagDeletedSuccess'));
      loadQATags();

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error deleting tag:', err);
      setError(t('qa.tagDeleteFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseTagDialog = () => {
    setShowTagDialog(false);
  };

  if (loading && qaItems.length === 0) {
    return <div className="flex justify-center py-10">{t('qa.loadingItems')}</div>;
  }

  // Count QA items by language
  const languageCounts = {};
  qaItems.forEach(item => {
    if (languageCounts[item.language_code]) {
      languageCounts[item.language_code]++;
    } else {
      languageCounts[item.language_code] = 1;
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-700">{t('qa.title')}</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleShowTagDialog}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
            disabled={isEditing || isImporting}
          >
            {t('qa.manageTags')}
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
            disabled={isEditing || isImporting || isExporting || isExportingHTML}
          >
            {isExporting ? (t('qa.exporting') || 'Exporting...') : (t('qa.exportJSON') || 'Export JSON')}
          </button>
          <button
            onClick={handleExportHTML}
            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
            disabled={isEditing || isImporting || isExporting || isExportingHTML}
          >
            {isExportingHTML ? (t('qa.exporting') || 'Exporting...') : (t('qa.exportHTML') || 'Export HTML')}
          </button>
          <button
            onClick={handleImportClick}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            disabled={isImporting || isEditing}
          >
            {isImporting ? t('qa.importing') : t('qa.importFromJson')}
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".json,application/json"
            onChange={handleFileChange}
          />
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={isEditing}
          >
            {t('qa.addNewQaItem')}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {!isEditing && (
        <div className="bg-gray-50 p-4 rounded">
          <div className="flex flex-wrap items-center space-x-2 space-y-2 sm:space-y-0">
            <label className="text-sm font-medium text-gray-700">
              {t('qa.filterByLanguage')}
            </label>
            <select
              value={languageFilter}
              onChange={handleLanguageFilterChange}
              className="p-2 border rounded"
            >
              <option value="all">{t('qa.allLanguages')}</option>
              {/* Always show zh-HK first if available */}
              {languageCounts['zh_HK'] > 0 && (
                <option value="zh_HK">
                  {t('qa.chineseTraditional')} ({languageCounts['zh_HK']})
                </option>
              )}
              {/* Show other languages */}
              {languageCodes.map(language => (
                language.value !== 'zh_HK' && languageCounts[language.value] > 0 && (
                  <option key={language.value} value={language.value}>
                    {language.label} ({languageCounts[language.value] || 0})
                  </option>
                )
              ))}
            </select>

            <label className="text-sm font-medium text-gray-700 ml-4">
              {t('qa.filterByTag')}
            </label>
            <select
              value={tagFilter}
              onChange={handleTagFilterChange}
              className="p-2 border rounded"
            >
              <option value="all">{t('qa.allTags')}</option>
              {qaTags.map(tag => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>

            <div className="ml-auto flex">
              <form onSubmit={handleSearch} className="flex">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder={t('qa.searchQuestionsAnswers')}
                  className="p-2 border rounded-l w-64"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-r hover:bg-gray-300"
                  disabled={isSearching}
                >
                  {isSearching ? 'Searching...' : t('qa.search')}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {isEditing ? (
        <div className="bg-white p-4 rounded shadow-sm">
          <h4 className="text-md font-medium mb-4">
            {currentQAItem ? t('qa.editQaItem') : t('qa.addNewQaItem')}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('qa.language')}
              </label>
              <select
                name="language_code"
                value={formData.language_code}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                required
              >
                {languageCodes.map(language => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {t('qa.selectLanguageForQaItem')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('qa.question')}
              </label>
              <textarea
                name="question"
                value={formData.question}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                rows="2"
                required
                placeholder={t('qa.enterQuestion')}
                style={{ whiteSpace: 'pre-wrap' }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('qa.questionPatientsMightAsk')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('qa.answer')}
              </label>
              <textarea
                name="answer"
                value={formData.answer}
                onChange={handleFormChange}
                className="w-full p-2 border rounded font-mono"
                rows="4"
                required
                placeholder={t('qa.enterAnswer')}
                style={{ whiteSpace: 'pre-wrap' }}
                maxLength={1000}
              ></textarea>
              <div className="mt-1 flex justify-between items-center">
                <p className="text-xs text-gray-500">
                  {t('qa.answerProvidedToQuestion')}
                </p>
                <p className={`text-xs ${formData.answer.length > 1000 ? 'text-red-600' : 'text-gray-500'}`}>
                  {formData.answer.length}/1000
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('qa.attachRule')}
              </label>
              <select
                name="attach_rule"
                value={formData.attach_rule}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                required
              >
                {attachRules.map(rule => (
                  <option key={rule.value} value={rule.value}>
                    {rule.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {t('qa.whenQaItemShouldBeIncluded')}
                <ul className="list-disc pl-5 mt-1">
                  <li>{t('qa.alwaysInclude')}</li>
                  <li>{t('qa.ifLanguageMatchInclude')}</li>
                  <li>{t('qa.onHoldDoNotUse')}</li>
                </ul>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('qa.tags')}
              </label>
              <AutoCompleteMultiSelect
                options={qaTags}
                selectedValues={formData.tag_ids}
                onChange={handleTagSelectionChange}
                placeholder={t('qa.selectTagsToCategorizeQaItem')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('qa.selectTagsToCategorizeQaItem')}. {t('qa.typeToSearchAndSelectTags')}.
              </p>
            </div>

            <div className="flex space-x-2 pt-4">
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                {isSaving ? t('qa.saving') : (currentQAItem ? t('qa.update') : t('qa.save'))}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                {t('qa.cancel')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {filteredQAItems.length === 0 ? (
            <div className="p-4 bg-gray-50 text-center rounded">
              {qaItems.length === 0 ? (
                t('qa.noQaKnowledgeBaseItemsFound')
              ) : (
                `${t('qa.noQaItemsFoundForSelectedFilters')}. ${languageFilter !== 'all' || tagFilter !== 'all' ? t('qa.trySelectingDifferentFilters') : ""}`
              )}
            </div>
          ) : (
            <table className="min-w-full bg-white border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border text-left w-2/12">{t('qa.statusInfo')}</th>
                  <th className="py-2 px-4 border text-left w-3/12">{t('qa.question')}</th>
                  <th className="py-2 px-4 border text-left w-4/12">{t('qa.answer')}</th>
                  <th className="py-2 px-4 border text-left w-3/12">{t('qa.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredQAItems.map((qaItem) => (
                  <tr key={qaItem.id} className="border-t">
                    <td className="py-2 px-4 border">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          qaItem.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {qaItem.is_active ? t('qa.active') : t('qa.inactive')}
                      </span>

                      <div className="mt-2 space-y-1 text-xs">
                        <div className="text-gray-600">
                          <span className="font-medium">{t('qa.rule')}</span> {attachRules.find(r => r.value === qaItem.attach_rule)?.label || qaItem.attach_rule}
                        </div>
                        <div className="text-gray-600">
                          <span className="font-medium">{t('qa.lang')}</span> {languageCodes.find(l => l.value === qaItem.language_code)?.label || qaItem.language_code}
                        </div>
                        {qaItem.tags && qaItem.tags.length > 0 && (
                          <div className="text-gray-600">
                            <span className="font-medium">{t('qa.tags')}</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {qaItem.tags.map(tag => (
                                <span
                                  key={tag.id}
                                  className="px-1 py-0.5 text-xs rounded"
                                  style={{ backgroundColor: tag.color + '20', color: tag.color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-4 border">
                      <div
                        className="max-w-xs overflow-hidden text-ellipsis cursor-pointer hover:text-blue-600"
                        onClick={() => handleOpenDialog(qaItem)}
                        title={qaItem.question}
                      >
                        {formatTextWithLineBreaks(qaItem.question)}
                      </div>
                    </td>
                    <td className="py-2 px-4 border">
                      <div
                        className="max-h-36 overflow-y-auto max-w-2xl cursor-pointer hover:text-blue-600"
                        onClick={() => handleOpenDialog(qaItem)}
                      >
                        {formatTextWithLineBreaks(qaItem.answer)}
                      </div>
                    </td>
                    <td className="py-2 px-4 border">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(qaItem)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          {t('edit')}
                        </button>
                        <button
                          onClick={() => handleToggleStatus(qaItem)}
                          className={`px-2 py-1 text-xs ${
                            qaItem.is_active
                              ? 'bg-orange-500 text-white hover:bg-orange-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          } rounded`}
                        >
                          {qaItem.is_active ? t('qa.deactivate') : t('qa.activate')}
                        </button>
                        <button
                          onClick={() => handleDelete(qaItem.id)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          {t('qa.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Dialog for displaying full content */}
      {showDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()} // Prevent clicks on the dialog content from closing it
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">{t('qa.qaItemDetails')}</h3>
              <button
                onClick={handleCloseDialog}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="mb-6">
                <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600 mb-2 inline-block">
                  {dialogContent.language}
                </span>
                <div className="font-medium text-gray-700 mb-1">{t('qa.question')}</div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  {formatTextWithLineBreaks(dialogContent.question)}
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-700 mb-1">{t('qa.answer')}</div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap">
                  {formatTextWithLineBreaks(dialogContent.answer)}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                {t('qa.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Management Dialog */}
      {showTagDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseTagDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">{t('qa.manageTags')}</h3>
              <button
                onClick={handleCloseTagDialog}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              {isEditingTag ? (
                <form onSubmit={handleTagSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('qa.tagName')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={tagFormData.name}
                      onChange={handleTagFormChange}
                      className="w-full p-2 border rounded"
                      required
                      placeholder={t('qa.enterTagName')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('qa.color')}
                    </label>
                    <input
                      type="color"
                      name="color"
                      value={tagFormData.color}
                      onChange={handleTagFormChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div className="flex space-x-2 pt-4">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                    >
                      {isSaving ? t('qa.saving') : (currentTag ? t('qa.update') : t('qa.create'))}
                    </button>
                    <button
                      type="button"
                      onClick={handleTagCancel}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      {t('qa.cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-md font-medium">{t('qa.existingTags')}</h4>
                    <button
                      onClick={handleAddTag}
                      className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      {t('qa.addNewTag')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {qaTags.map(tag => (
                      <div key={tag.id} className="flex items-center justify-between p-3 border rounded">
                        <div className="flex items-center space-x-2">
                          <span
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: tag.color }}
                          ></span>
                          <span className="font-medium">{tag.name}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditTag(tag)}
                            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            {t('edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteTag(tag.id)}
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                    {qaTags.length === 0 && (
                      <div className="text-center text-gray-500 py-4">
                        {t('qa.noTagsCreatedYet')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QAKnowledgeBaseSection;
